import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid, getGridStringOperators } from "@mui/x-data-grid";
import { styled } from "@mui/material";
import { gridGreekLocales } from "./gridLocales";

// MUI Community has no column pinning (Pro-only), so these fields are
// pinned via CSS by field name — offsets must match the widths below.
const PINNED_LEFT_FIELDS = ["__check__", "lastName", "firstName", "taxNumber"];
const PINNED_LEFT_OFFSETS = { __check__: 0, lastName: 50, firstName: 180, taxNumber: 310 };
const PINNED_LEFT_Z_INDEX = 5;

const pinnedLeftSelector = (elementSelector) =>
  PINNED_LEFT_FIELDS.map(
    (field) => `& ${elementSelector}[data-field="${field}"]`
  ).join(", ");

const pinnedLeftOffsetRules = (elementSelector) =>
  PINNED_LEFT_FIELDS.reduce((rules, field) => {
    rules[`& ${elementSelector}[data-field="${field}"]`] = {
      left: PINNED_LEFT_OFFSETS[field],
    };
    return rules;
  }, {});

const DataGridStyled = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainer":
    {
      justifyContent: "center",
    },
  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    justifyContent: "center",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    textAlign: "center",
  },
  "& .MuiDataGrid-cell": {
    justifyContent: "center",
    textAlign: "center",
  },
  "& .MuiDataGrid-cell:focus": {
    outline: "none",
  },
  "& .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-overlayWrapperInner": {
    background: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiDataGrid-footerContainer": {
    "& .MuiTablePagination-toolbar": {
      paddingRight: "4rem", // Allow room for the chatbot
    },
  },
  [pinnedLeftSelector(".MuiDataGrid-columnHeader")]: {
    position: "sticky",
    zIndex: PINNED_LEFT_Z_INDEX + 1,
    backgroundColor: "var(--DataGrid-containerBackground)",
  },
  [pinnedLeftSelector(".MuiDataGrid-cell")]: {
    position: "sticky",
    zIndex: PINNED_LEFT_Z_INDEX,
    backgroundColor: "var(--DataGrid-containerBackground)",
  },
  ...pinnedLeftOffsetRules(".MuiDataGrid-columnHeader"),
  ...pinnedLeftOffsetRules(".MuiDataGrid-cell"),
  [pinnedLeftSelector(".MuiDataGrid-row:hover .MuiDataGrid-cell")]: {
    backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
  },
  [pinnedLeftSelector(".MuiDataGrid-row.Mui-selected .MuiDataGrid-cell")]: {
    backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
  },
}));

const filterPanelSlotProps = {
  filterPanel: {
    sx: {
      "& .MuiDataGrid-filterForm": {
        alignItems: "flex-end",
        gap: "0.5rem",
        "& .MuiDataGrid-filterFormColumnInput ": {
          "& label": {
            top: "-1.5rem",
          },
          "& legend": {
            display: "none",
          },
        },
        "& .MuiDataGrid-filterFormOperatorInput ": {
          "& label": {
            top: "-1.5rem",
          },
          "& legend": {
            display: "none",
          },
        },
        "& .MuiDataGrid-filterFormValueInput ": {
          "& label": {
            top: "0.5rem",
            fontSize: "1rem",
            margin: 0,
          },
          "& legend": {
            display: "none",
          },
        },
      },
    },
    filterFormProps: {
      valueInputProps: {
        InputComponentProps: {
          variant: "outlined",
          size: "small",
        },
      },
    },
  },
};

const applyMissingFilterTranslations = (t, columns) => {
  const gridStringOperators = getGridStringOperators();

  return columns.map((col) => {
    if (col.type === "number" || col.filterable === false) {
      return col;
    }

    return {
      ...col,
      filterOperators: gridStringOperators.map((operator) => {
        if (operator.value === "doesNotContain") {
          return {
            ...operator,
            label: t("DataGrid.Filters.DoesNotContain.Label"),
          };
        }
        if (operator.value === "doesNotEqual") {
          return {
            ...operator,
            label: t("DataGrid.Filters.DoesNotEqual.Label"),
          };
        }
        return operator;
      }),
    };
  });
};

function DataTableUnstyled({
  columns = [],
  rows = [],
  initialState = {},
  className,
  isLoading,
  filterModel,
  sortModel,
  disableRowSelectionOnClick,
  setSelectedRows,
  onSortModelChange,
  onFiltersChange,
}) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  const finalColumns = useMemo(() => {
    return applyMissingFilterTranslations(t, columns);
  }, [columns, t]);

  const handleSelection = (ids, details) => {
    const gridApi = details.api;
    const rowsMap = gridApi?.getSelectedRows();
    if (rowsMap) {
      const rows = Array.from(rowsMap.values());
      setSelectedRows(rows);
    } else {
      setSelectedRows(ids);
    }
  };

  return (
    <DataGridStyled
      localeText={isEn ? {} : gridGreekLocales}
      loading={isLoading}
      rows={rows}
      columns={finalColumns}
      initialState={initialState}
      pageSizeOptions={[5, 10, 20, 50, 100]}
      disableVirtualization
      checkboxSelection
      slotProps={filterPanelSlotProps}
      filterModel={filterModel}
      sortModel={sortModel}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      className={className}
      onRowSelectionModelChange={handleSelection}
      onFilterModelChange={onFiltersChange}
      onSortModelChange={onSortModelChange}
    />
  );
}

const DataTable = styled(DataTableUnstyled)({});
export default DataTable;
